/**
 * Onboarding Step Registry — IN-2.b / IN-2.c / IN-2.d / IN-2.e / IN-2.f
 * ─────────────────────────────────────────────────────────────────────
 * Pure data: ordered list of steps available to a session.
 *
 * Per-entity inclusion is *derived* via `appliesTo` guards. No
 * duplicate wizard exists — every entity renders the same shared
 * step components from the same writers.
 *
 * Supported flows:
 *   • independent / licensed_contractor  → identity → service_area → documents → agreement → payout → review
 *   • internal_w2                        → identity → service_area (employment) → documents → agreement → payout → review
 *   • manufacturer_partner               → identity → documents → agreement → payout → review
 *   • branch (IN-2.f)                    → identity → parent_confirmation → inherited_compliance_preview → branch_overrides → agreement → payout (parent_routed) → review
 *   • crew   (IN-2.f)                    → identity → parent_confirmation → inherited_compliance_preview → documents (bg check) → agreement → payout (parent_routed) → review
 *   • helper (IN-2.f)                    → identity → parent_confirmation → minimal_info → agreement → payout (parent_routed) → review
 *
 * Rules:
 *   • No UI in this file. Step renderers live in the wizard shell.
 *   • Per-entity inclusion lives here; per-entity *field-level* copy
 *     lives in the renderer.
 */

import type { OnboardingEntityType, OnboardingSession } from "./types";

export type OnboardingStepKey =
  | "identity"
  | "service_area"
  | "parent_confirmation"
  | "inherited_compliance_preview"
  | "branch_overrides"
  | "minimal_info"
  | "documents"
  | "agreement"
  | "payout"
  | "review";

export interface OnboardingStepDescriptor {
  key: OnboardingStepKey;
  title: string;
  appliesTo?: (entityType: OnboardingEntityType) => boolean;
  required: boolean;
}

const CHILD_ENTITY_TYPES: ReadonlySet<OnboardingEntityType> = new Set([
  "branch",
  "crew",
  "helper",
]);
const isChild = (et: OnboardingEntityType) => CHILD_ENTITY_TYPES.has(et);

/** Per-entity title overrides. Falls back to the descriptor's base title. */
const TITLE_OVERRIDES: Partial<
  Record<OnboardingStepKey, Partial<Record<OnboardingEntityType, string>>>
> = {
  identity: {
    internal_w2: "Employee identity",
    manufacturer_partner: "Partner organization",
    branch: "Branch identity",
    crew: "Crew identity",
    helper: "Helper identity",
  },
  service_area: {
    internal_w2: "Employment details",
  },
  documents: {
    internal_w2: "Employment documents",
    manufacturer_partner: "Certification documents",
    crew: "Background check",
  },
  agreement: {
    internal_w2: "Offer letter",
    manufacturer_partner: "Certification agreement",
    branch: "Branch sublicense",
    crew: "Crew acknowledgment",
    helper: "Helper acknowledgment",
  },
  payout: {
    internal_w2: "Payroll setup",
    branch: "Payout routing",
    crew: "Payout routing",
    helper: "Payout routing",
  },
};

const REGISTRY: OnboardingStepDescriptor[] = [
  { key: "identity", title: "Identity & contact", required: true },
  {
    key: "service_area",
    title: "Service area & trade",
    required: false,
    // Manufacturer partners don't field-install. Child entities inherit
    // service area from the parent (no per-child collection).
    appliesTo: (et) => et !== "manufacturer_partner" && !isChild(et),
  },
  {
    key: "parent_confirmation",
    title: "Parent entity confirmation",
    required: true,
    appliesTo: (et) => isChild(et),
  },
  {
    key: "inherited_compliance_preview",
    title: "Inherited compliance",
    required: true,
    appliesTo: (et) => et === "branch" || et === "crew",
  },
  {
    key: "branch_overrides",
    title: "Branch overrides",
    required: false,
    appliesTo: (et) => et === "branch",
  },
  {
    key: "minimal_info",
    title: "A few details",
    required: true,
    appliesTo: (et) => et === "helper",
  },
  {
    key: "documents",
    title: "Compliance documents",
    required: true,
    // Branch inherits all docs from parent (no own collection in IN-2.f).
    // Helper has no documents step (minimal_info covers it).
    appliesTo: (et) => et !== "branch" && et !== "helper",
  },
  { key: "agreement", title: "Agreement & signature", required: true },
  { key: "payout", title: "Payout method", required: true },
  { key: "review", title: "Review & submit", required: true },
];

export function getOnboardingSteps(
  session: Pick<OnboardingSession, "entityType">,
): OnboardingStepDescriptor[] {
  return REGISTRY.filter(
    (s) => !s.appliesTo || s.appliesTo(session.entityType),
  ).map((s) => {
    const override = TITLE_OVERRIDES[s.key]?.[session.entityType];
    return override ? { ...s, title: override } : s;
  });
}

export function getStepIndex(
  session: Pick<OnboardingSession, "entityType">,
  key: string,
): number {
  return getOnboardingSteps(session).findIndex((s) => s.key === key);
}

export function getNextStepKey(
  session: Pick<OnboardingSession, "entityType">,
  current: string,
): string | undefined {
  const steps = getOnboardingSteps(session);
  const i = steps.findIndex((s) => s.key === current);
  if (i < 0 || i >= steps.length - 1) return undefined;
  return steps[i + 1].key;
}

export function getPreviousStepKey(
  session: Pick<OnboardingSession, "entityType">,
  current: string,
): string | undefined {
  const steps = getOnboardingSteps(session);
  const i = steps.findIndex((s) => s.key === current);
  if (i <= 0) return undefined;
  return steps[i - 1].key;
}
